import { createSlice, createAction, createReducer } from '@reduxjs/toolkit';
import { COMPONENT_TYPES, MENU_TYPES } from '../constants';
import { getAllLayoutNames } from '../layouts/LayoutRegistry';
import { getLayoutDefaultPropsById } from '../layouts';

const initialState = {
  components: [],  // 캔버스에 추가된 컴포넌트 목록
  selectedComponentId: null,  // 현재 선택된 컴포넌트의 ID
  canvasSize: { width: 1200, height: 800 },  // 캔버스 크기
  projectName: '새 프로젝트',  // 프로젝트 이름
  savedProjects: [],  // 저장된 프로젝트 목록
  layout: {
    selectedLayout: '', // 선택된 레이아웃 ID
    layoutProps: {}     // 레이아웃 속성
  },
  menus: [],           // 메뉴 목록
  selectedMenuId: null // 선택된 메뉴 ID
};

// 컴포넌트 타입별 기본 크기 설정
const defaultComponentSizes = {
  [COMPONENT_TYPES.TEXT]: { width: '100%', height: 40 },
  [COMPONENT_TYPES.IMAGE]: { width: '100%', height: 150 },
  [COMPONENT_TYPES.CONTAINER]: { width: '100%', height: 200 },
  [COMPONENT_TYPES.BUTTON]: { width: '100%', height: 40 },
  [COMPONENT_TYPES.LOGIN]: { width: '100%', height: 300 },
  [COMPONENT_TYPES.BOARD]: { width: '100%', height: 400 },
  [COMPONENT_TYPES.DETAIL_PAGE]: { width: '100%', height: 500 },
  [COMPONENT_TYPES.ROW]: { width: '100%', height: 70 },
  [COMPONENT_TYPES.COLUMN]: { width: '100%', height: 150 },
  [COMPONENT_TYPES.USER_PROFILE]: { width: '100%', height: 600 },
};

// 컴포넌트 타입별 기본 데이터
const defaultComponentData = {
  [COMPONENT_TYPES.TEXT]: '',
  [COMPONENT_TYPES.IMAGE]: { src: 'https://via.placeholder.com/150', alt: '이미지' },
  [COMPONENT_TYPES.CONTAINER]: '',
  [COMPONENT_TYPES.BUTTON]: '버튼',
  [COMPONENT_TYPES.LOGIN]: { 
    title: '로그인', 
    apiUrl: 'https://api.example.com/login' 
  },
  [COMPONENT_TYPES.BOARD]: { 
    title: '게시판', 
    parameter: '1',
    items: [] 
  },
  [COMPONENT_TYPES.DETAIL_PAGE]: { 
    productId: '1',
    apiUrl: 'https://api.example.com/products'
  },
  [COMPONENT_TYPES.ROW]: {
    gutter: 30,
    verticalAlign: 'top',
    horizontalAlign: 'left',
    width: '100%'
  },
  [COMPONENT_TYPES.COLUMN]: {
    xs: 12,
    sm: undefined,
    md: undefined,
    lg: undefined,
    xl: undefined,
    offset: 0,
    gutter: 30,
    width: '100%'
  },
  [COMPONENT_TYPES.USER_PROFILE]: { 
    title: '회원 정보',
  },
};

// 컴포넌트 타입별 기본 스타일
const defaultComponentStyles = {
  [COMPONENT_TYPES.TEXT]: { 
    color: '#000000', 
    fontSize: '16px', 
    fontWeight: 'normal',
    paddingLeft: '0px',
    paddingRight: '0px' 
  },
  [COMPONENT_TYPES.IMAGE]: { 
    border: 'none', 
    borderRadius: '0px',
    paddingLeft: '0px',
    paddingRight: '0px' 
  },
  [COMPONENT_TYPES.CONTAINER]: { 
    backgroundColor: 'transparent', 
    border: 'none', 
    borderRadius: '0px',
    paddingLeft: '0px',
    paddingRight: '0px' 
  },
  [COMPONENT_TYPES.BUTTON]: { 
    backgroundColor: '#4a90e2', 
    color: '#ffffff', 
    border: 'none', 
    borderRadius: '4px', 
    padding: '8px 16px', 
    fontSize: '14px',
    paddingLeft: '0px',
    paddingRight: '0px' 
  },
  [COMPONENT_TYPES.LOGIN]: { 
    backgroundColor: 'transparent', 
    borderRadius: '0px', 
    boxShadow: 'none', 
    padding: '0px',
    buttonColor: '#4a90e2',
    paddingLeft: '0px',
    paddingRight: '0px'
  },
  [COMPONENT_TYPES.BOARD]: { 
    backgroundColor: 'transparent', 
    borderRadius: '0px', 
    boxShadow: 'none', 
    padding: '0px',
    paddingLeft: '0px',
    paddingRight: '0px'
  },
  [COMPONENT_TYPES.DETAIL_PAGE]: { 
    backgroundColor: 'transparent', 
    borderRadius: '0px', 
    boxShadow: 'none', 
    padding: '0px',
    buttonColor: '#4a90e2',
    paddingLeft: '0px',
    paddingRight: '0px'
  },
  [COMPONENT_TYPES.ROW]: {
    backgroundColor: 'transparent',
    paddingLeft: '0px',
    paddingRight: '0px'
  },
  [COMPONENT_TYPES.COLUMN]: {
    backgroundColor: 'transparent',
    paddingLeft: '0px',
    paddingRight: '0px'
  }
};

export const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    addComponent: (state, action) => { 
      const component = action.payload;
      
      
      // 유효한 컴포넌트 타입인지 확인 - 문자열 타입도 지원하도록 수정
      const validTypes = Object.values(COMPONENT_TYPES);
      const validTypeKeys = Object.keys(COMPONENT_TYPES);
      const componentTypeStr = String(component.type).toUpperCase();
      
      const isValidType = 
        component.type && 
        (validTypes.includes(component.type) || 
         validTypeKeys.includes(componentTypeStr) ||
         validTypes.includes(componentTypeStr));
      
      if (!isValidType) {
        console.error('유효하지 않은 컴포넌트 타입:', component.type, '유효한 타입:', validTypes);
        return; // 유효하지 않은 타입이면 추가하지 않음
      }
      
      // 컴포넌트 타입 정규화 (문자열을 상수로 변환)
      let normalizedType = component.type;
      if (typeof component.type === 'string') {
        // 문자열이 "ROW", "COLUMN" 등의 형태일 경우 COMPONENT_TYPES 상수로 변환
        normalizedType = COMPONENT_TYPES[componentTypeStr] || component.type;
      }
      
      // 컴포넌트 타입에 따른 기본 크기 설정
      const defaultSize = defaultComponentSizes[normalizedType] || { width: 200, height: 100 };
      
      // 컴포넌트 타입에 따른 기본 스타일 설정
      const componentStyle = { ...defaultComponentStyles[normalizedType], ...component.style };
      
      // 기본 데이터 설정
      let defaultData = defaultComponentData[normalizedType] || {};
      
      // 컴포넌트 추가
      const newComponent = {
        id: component.id,
        type: normalizedType, // 정규화된 타입 사용
        position: component.position || { x: 0, y: 0 },
        size: component.size || defaultSize,
        style: componentStyle,
        content: component.content !== undefined ? component.content : '', // 내용이 빈 문자열이 될 수 있도록 수정
        data: component.data || defaultData,
        parentId: component.parentId || null // 부모 컴포넌트 ID
      };
      
      
      state.components.push(newComponent);
      state.selectedComponentId = component.id;
    },
    updateComponent: (state, action) => {
      const { id, changes } = action.payload;
      const component = state.components.find(comp => comp.id === id);
      if (component) {
        Object.assign(component, changes);
      }
    },
    removeComponent: (state, action) => {
      const componentId = action.payload;
      
      // 삭제할 컴포넌트의 자식 컴포넌트들도 함께 삭제
      const componentsToRemove = [componentId];
      
      // 자식 컴포넌트들 찾기 (재귀적으로)
      const findChildren = (parentId) => {
        state.components.forEach(comp => {
          if (comp.parentId === parentId) {
            componentsToRemove.push(comp.id);
            findChildren(comp.id);
          }
        });
      };
      
      findChildren(componentId);
      
      // 모든 관련 컴포넌트 삭제
      state.components = state.components.filter(comp => !componentsToRemove.includes(comp.id));
      
      if (state.selectedComponentId === componentId) {
        state.selectedComponentId = null;
      }
    },
    loadComponents: (state, action) => {  
      state.components = action.payload;
    },
    selectComponent: (state, action) => {
      // 이전에 선택된 컴포넌트의 선택 상태 해제
      state.components.forEach(comp => {
        comp.isSelected = comp.id === action.payload;
      });
      state.selectedComponentId = action.payload;
    },
    applyPcSettingsToMobile: (state, action) => {
      const { componentId } = action.payload;
      const component = state.components.find(comp => comp.id === componentId);
      
      if (!component) return;
      
      console.log('컴포넌트 구조 확인:', JSON.stringify(component, null, 2));
      
      // 1. 일반 스타일 속성 처리
      if (component.style) {
        // 모바일 스타일 객체가 없으면 생성
        if (!component.mobileStyle) {
          component.mobileStyle = { ...component.style };
          console.log('일반 스타일을 모바일 스타일로 복사:', component.mobileStyle);
        } else {
          // 기존 모바일 스타일에 PC 스타일 병합
          component.mobileStyle = {
            ...component.mobileStyle,
            ...component.style,
            // 모바일에서 width는 보통 100%로 설정
            width: component.mobileStyle.width || '100%'
          };
          console.log('PC 스타일을 모바일 스타일에 병합:', component.mobileStyle);
        }
      }
      
      // 2. device 속성이 있는 경우 (BoardComponent 등)
      if (component.style && component.style.device) {
        // PC 스타일 설정을 가져옵니다
        const pcStyle = component.style.device.pc || {};
        
        // 모바일 스타일 객체가 없으면 생성
        if (!component.style.device.mobile) {
          component.style.device.mobile = {};
        }
        
        // PC 설정을 모바일에 복사
        component.style.device.mobile = {
          ...component.style.device.mobile,
          ...pcStyle
        };
        
        console.log('device 스타일 복사 완료:', 
          { pc: pcStyle, mobile: component.style.device.mobile });
      }
      
      // 3. deviceProperty가 있는 경우 (ImageTextComponent, FixedBgTextComponent 등)
      if (component.content && typeof component.content === 'object') {
        // 3.1 string content를 객체로 변환해야 하는 경우
        if (!component.content.deviceProperty && typeof component.content !== 'string') {
          component.content.deviceProperty = {
            pc: JSON.stringify(component.content),
            mobile: JSON.stringify(component.content)
          };
          console.log('content를 deviceProperty로 변환:', component.content.deviceProperty);
        }
        // 3.2 deviceProperty가 있는 경우
        else if (component.content.deviceProperty) {
          // PC 내용 설정을 가져옵니다
          const pcContent = component.content.deviceProperty.pc;
          
          if (pcContent) {
            // PC 설정을 모바일에 복사
            component.content.deviceProperty.mobile = pcContent;
            console.log('deviceProperty content 복사:', 
              { pc: pcContent, mobile: component.content.deviceProperty.mobile });
          }
        }
      }
      
      // 4. data 객체에 device 속성이 있는 경우
      if (component.data && component.data.device) {
        // PC 데이터 설정을 가져옵니다
        const pcData = component.data.device.pc || {};
        
        // 모바일 데이터 객체가 없으면 생성
        if (!component.data.device.mobile) {
          component.data.device.mobile = {};
        }
        
        // PC 설정을 모바일에 복사
        component.data.device.mobile = {
          ...component.data.device.mobile,
          ...pcData
        };
        
        console.log('device data 복사 완료:', 
          { pc: pcData, mobile: component.data.device.mobile });
      }
      
      // 5. 일반 data 속성이 있고 device 속성이 없는 경우
      if (component.data && !component.data.device) {
        // 필요한 경우 device 구조 생성
        component.data.device = {
          pc: { ...component.data },
          mobile: { ...component.data }
        };
        
        console.log('일반 data를 device 구조로 변환:', component.data.device);
      }
      
      // 6. 크기 속성 처리 - width는 모바일에서 보통 100%
      if (component.size) {
        if (!component.mobileSize) {
          component.mobileSize = { 
            ...component.size,
            width: '100%' // 모바일에서는 기본적으로 전체 너비 사용
          };
        } else {
          component.mobileSize.height = component.size.height;
          component.mobileSize.width = '100%';
        }
        
        console.log('크기 설정 복사 완료:', 
          { pc: component.size, mobile: component.mobileSize });
      }
      
      console.log('PC → 모바일 설정 복사 완료:', component);
    },
    setProjectName: (state, action) => {
      state.projectName = action.payload;
    },
    saveProject: (state) => {
      const projectToSave = {
        id: Date.now().toString(),
        name: state.projectName,
        components: [...state.components],
        menus: [...state.menus], // 메뉴 정보 추가
        canvasSize: { ...state.canvasSize },
        layout: { ...state.layout },
        timestamp: new Date().toISOString()
      };
      
      // 기존 프로젝트를 덮어쓰기 또는 새로 추가
      const existingIndex = state.savedProjects.findIndex(p => p.name === state.projectName);
      if (existingIndex >= 0) {
        state.savedProjects[existingIndex] = projectToSave;
      } else {
        state.savedProjects.push(projectToSave);
      }
      
      // 로컬 스토리지에도 저장
      localStorage.setItem('savedProjects', JSON.stringify(state.savedProjects));
    },
    loadProject: (state, action) => {
      const project = state.savedProjects.find(p => p.id === action.payload);
      if (project) {
        state.components = [...project.components];
        state.menus = project.menus ? [...project.menus] : []; // 메뉴 정보 로드
        state.projectName = project.name;
        state.canvasSize = { ...project.canvasSize };
        state.layout = project.layout ? { ...project.layout } : initialState.layout;
        state.selectedComponentId = null;
        state.selectedMenuId = null;
      }
    },
    loadSavedProjects: (state) => {
      const saved = localStorage.getItem('savedProjects');
      if (saved) {
        state.savedProjects = JSON.parse(saved);
      }
    },
    deleteProject: (state, action) => {
      state.savedProjects = state.savedProjects.filter(p => p.id !== action.payload);
      localStorage.setItem('savedProjects', JSON.stringify(state.savedProjects));
    },
    updateComponentPosition: (state, action) => {
      const { id, newPosition, parentId, size, order } = action.payload;
      const component = state.components.find(comp => comp.id === id);
      if (component) {
        // 항상 x 위치를 0으로 설정하여 가로로 전체 너비를 사용할 수 있게 함
        component.position = {
          x: 0,
          y: newPosition.y
        };
        
        // parentId가 지정되었다면 업데이트 (부모 컴포넌트 변경)
        if (parentId !== undefined) {
          component.parentId = parentId;
        }
        
        // order가 지정되었다면 업데이트 (컴포넌트 순서 변경)
        if (order !== undefined) {
          component.order = order;
        }
        
        // size가 지정되었다면 컴포넌트 크기 업데이트
        if (size) {
          component.size = {
            width: '100%',  // 항상 width를 100%로 설정
            height: size.height
          };
        } else {
          // size를 지정하지 않았더라도 width는 100%로 설정
          component.size = {
            width: '100%',
            height: component.size.height
          };
        }
      }
    },
    exportProjectToJSON: (state) => {
      const projectData = {
        name: state.projectName,
        components: state.components,
        canvasSize: state.canvasSize,
        layout: state.layout,
        timestamp: new Date().toISOString()
      };
      
      // JSON 문자열로 변환
      const jsonString = JSON.stringify(projectData, null, 2);
      
      // 다운로드 링크 생성 및 클릭
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${state.projectName.replace(/\s+/g, '-').toLowerCase()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    importProjectFromJSON: (state, action) => {
      const projectData = action.payload;
      if (projectData && projectData.components) {
        state.projectName = projectData.name || '가져온 프로젝트';
        state.components = projectData.components;
        state.canvasSize = projectData.canvasSize || state.canvasSize;
        if (projectData.layout) {
          state.layout = projectData.layout;
        } else {
          state.layout = initialState.layout;
        }
        state.selectedComponentId = null;
      }
    },
    selectLayout: (state, action) => {
      const layoutId = action.payload;
      state.layout.selectedLayout = layoutId;
      // 레이아웃 속성 초기화
      state.layout.layoutProps = {};
    },
    updateLayoutProps: (state, action) => {
      state.layout.layoutProps = {
        ...state.layout.layoutProps,
        ...action.payload
      };
    },
    resetLayout: (state) => {
      state.layout.selectedLayout = '';
      state.layout.layoutProps = {};
    },
    // 메뉴 추가
    addMenu: (state, action) => {
      const menu = action.payload;
      
      // 'edit' 또는 'preview'가 포함된 경로는 허용하지 않음
      let path = menu.path || '';
      if (path.includes('/edit') || path.includes('/preview') || 
          path === '/edit' || path === '/preview') {
        console.error(`사용할 수 없는 경로입니다: ${path}`);
        path = '';
      }
      
      state.menus.push({
        id: menu.id,
        name: menu.name || '새 메뉴',
        path: path,
        type: menu.type || MENU_TYPES.MAIN,
        parentId: menu.parentId || null,
        order: menu.order || state.menus.length,
        visible: true, // 모든 메뉴 타입을 기본적으로 표시
        components: menu.components || [],
        externalLink: menu.externalLink || '', // 외부 링크 필드 추가
        menuAction: menu.menuAction || '' // 메뉴 액션 필드 추가 (예: 'logout')
      });
      
      state.selectedMenuId = menu.id;
    },
    
    // 메뉴 업데이트
    updateMenu: (state, action) => {
      const { id, changes } = action.payload;
      const menu = state.menus.find(menu => menu.id === id);
      if (menu) {
        // 경로 중복 체크
        if (changes.path !== undefined && menu.path !== changes.path) {
          // 'edit' 또는 'preview'가 포함된 경로는 허용하지 않음
          if (changes.path.includes('/edit') || changes.path.includes('/preview') || 
              changes.path === '/edit' || changes.path === '/preview') {
            console.error(`사용할 수 없는 경로입니다: ${changes.path}`);
            // path 속성을 제외하고 나머지 변경 사항만 적용
            const { path, ...otherChanges } = changes;
            Object.assign(menu, otherChanges);
            return;
          }
          
          // 홈 경로(/) 중복 체크
          if (changes.path === '/') {
            // 이미 다른 메뉴에 홈 경로가 있는지 확인
            const hasHomePath = state.menus.some(m => 
              m.id !== id && m.path === '/' && m.type === menu.type
            );
            
            if (hasHomePath) {
              console.error('홈(/) 경로는 한 번만 사용할 수 있습니다.');
              // path 속성을 제외하고 나머지 변경 사항만 적용
              const { path, ...otherChanges } = changes;
              Object.assign(menu, otherChanges);
              return;
            }
          } else {
            // 일반 경로 중복 체크
            const duplicatePath = state.menus.some(m => 
              m.id !== id && m.path === changes.path && changes.path !== ''
            );
            
            if (duplicatePath) {
              // 중복 경로는 적용하지 않음
              console.error(`중복된 경로입니다: ${changes.path}`);
              // path 속성을 제외하고 나머지 변경 사항만 적용
              const { path, ...otherChanges } = changes;
              Object.assign(menu, otherChanges);
              return;
            }
          }
        }
        
        Object.assign(menu, changes);
      }
    },
    
    // 메뉴 삭제
    removeMenu: (state, action) => {
      const menuId = action.payload;
      
      // 삭제할 메뉴의 자식 메뉴들도 함께 삭제
      const menusToRemove = [menuId];
      
      // 자식 메뉴들 찾기 (재귀적으로)
      const findChildren = (parentId) => {
        state.menus.forEach(menu => {
          if (menu.parentId === parentId) {
            menusToRemove.push(menu.id);
            findChildren(menu.id);
          }
        });
      };
      
      findChildren(menuId);
      
      // 모든 관련 메뉴 삭제
      state.menus = state.menus.filter(menu => !menusToRemove.includes(menu.id));
      
      if (state.selectedMenuId === menuId) {
        state.selectedMenuId = null;
      }
    },
    
    // 메뉴 선택
    selectMenu: (state, action) => {
      state.selectedMenuId = action.payload;
    },
    
    // 메뉴 순서 변경
    reorderMenus: (state, action) => {
      const { sourceId, destinationId, asChild, position, targetParentId } = action.payload;
      
      // 소스 메뉴 찾기
      const sourceMenuIndex = state.menus.findIndex(menu => menu.id === sourceId);
      if (sourceMenuIndex === -1) {
        console.error('소스 메뉴를 찾을 수 없음:', sourceId);
        return;
      }
      
      const sourceMenu = state.menus[sourceMenuIndex];
      
      // 최상위로 이동하는 경우 (destinationId가 없거나 'root'인 경우)
      if (!destinationId || destinationId === 'root') {
        // 소스 메뉴를 복제하고 변경
        const updatedSourceMenu = {
          ...sourceMenu,
          parentId: null
        };
        
        // 같은 타입의 최상위 메뉴 중 가장 높은 순서 값을 찾음
        const sameTypeRootMenus = state.menus.filter(
          menu => menu.type === sourceMenu.type && !menu.parentId && menu.id !== sourceId
        );
        
        const maxOrder = sameTypeRootMenus.length > 0
          ? Math.max(...sameTypeRootMenus.map(menu => menu.order || 0)) + 1
          : 0;
        
        // 순서 값 설정
        updatedSourceMenu.order = maxOrder;
        
        // 메뉴 배열 업데이트
        state.menus = [
          ...state.menus.slice(0, sourceMenuIndex),
          updatedSourceMenu,
          ...state.menus.slice(sourceMenuIndex + 1)
        ];
        
        return;
      }
      
      // 대상 메뉴 찾기
      const destinationMenuIndex = state.menus.findIndex(menu => menu.id === destinationId);
      if (destinationMenuIndex === -1) {
        console.error('대상 메뉴를 찾을 수 없음:', destinationId);
        return;
      }
      
      const destinationMenu = state.menus[destinationMenuIndex];
      
      // 메뉴 타입이 다른 경우 이동 불가
      if (sourceMenu.type !== destinationMenu.type) {
        console.error('다른 타입의 메뉴 간에는 순서를 변경할 수 없습니다');
        return;
      }
      
      // 깊이 계산 함수
      const getMenuDepth = (menuId) => {
        let depth = 0;
        let currentMenu = state.menus.find(m => m.id === menuId);
        
        while (currentMenu && currentMenu.parentId) {
          depth++;
          currentMenu = state.menus.find(m => m.id === currentMenu.parentId);
        }
        
        return depth;
      };
      
      // 하위 메뉴로 이동하는 경우, 최대 3단계 깊이 제한 확인
      if (asChild) {
        // 대상 메뉴의 깊이가 이미 2이상이면 더 이상 하위로 이동 불가
        if (getMenuDepth(destinationId) >= 2) {
          console.error('최대 3단계까지만 중첩이 가능합니다.');
          return;
        }
        
        // 순환 참조 방지 (자식이 부모를 포함하지 않도록)
        let parentId = destinationMenu.parentId;
        while (parentId) {
          if (parentId === sourceId) {
            console.error('순환 참조가 발생합니다: 부모 메뉴를 자식 메뉴로 이동할 수 없습니다.');
            return;
          }
          const parent = state.menus.find(m => m.id === parentId);
          if (!parent) break;
          parentId = parent.parentId;
        }
        
        // 같은 부모 내에서 가장 높은 순서 값을 찾음
        const siblings = state.menus.filter(
          menu => menu.parentId === destinationId && menu.id !== sourceId
        );
        
        const maxOrder = siblings.length > 0
          ? Math.max(...siblings.map(menu => menu.order || 0)) + 1
          : 0;
        
        // 소스 메뉴 업데이트
        const updatedSourceMenu = {
          ...sourceMenu,
          parentId: destinationId,
          order: maxOrder
        };
        
        // 메뉴 배열 업데이트
        state.menus = [
          ...state.menus.slice(0, sourceMenuIndex),
          updatedSourceMenu,
          ...state.menus.slice(sourceMenuIndex + 1)
        ];
      } 
      // 같은 레벨로 이동하는 경우
      else {
        // 타겟 부모 ID를 사용
        const targetParent = targetParentId || destinationMenu.parentId;
        
        // 형제 메뉴 가져오기
        const siblings = state.menus.filter(
          menu => menu.parentId === targetParent && menu.id !== sourceId
        );
        
        // 대상 메뉴 순서
        const targetOrder = destinationMenu.order || 0;
        
        // 업데이트할 소스 메뉴 생성
        let updatedSourceMenu = {
          ...sourceMenu,
          parentId: targetParent
        };
        
        // position이 지정된 경우 (before/after)
        if (position) {
          // 이전 위치에 넣을 경우
          if (position === 'before') {
            updatedSourceMenu.order = targetOrder;
            
            // 메뉴 배열 업데이트 (소스 메뉴 먼저 변경)
            state.menus = [
              ...state.menus.slice(0, sourceMenuIndex),
              updatedSourceMenu,
              ...state.menus.slice(sourceMenuIndex + 1)
            ];
            
            // 대상 메뉴보다 순서가 큰 모든 메뉴의 순서를 1씩 증가
            state.menus = state.menus.map(menu => {
              if (menu.id !== sourceId && menu.parentId === targetParent && (menu.order || 0) >= targetOrder) {
                return { ...menu, order: (menu.order || 0) + 1 };
              }
              return menu;
            });
          } 
          // 다음 위치에 넣을 경우
          else if (position === 'after') {
            updatedSourceMenu.order = targetOrder + 1;
            
            // 메뉴 배열 업데이트 (소스 메뉴 먼저 변경)
            state.menus = [
              ...state.menus.slice(0, sourceMenuIndex),
              updatedSourceMenu,
              ...state.menus.slice(sourceMenuIndex + 1)
            ];
            
            // 대상 메뉴보다 순서가 큰 모든 메뉴의 순서를 1씩 증가
            state.menus = state.menus.map(menu => {
              if (menu.id !== sourceId && menu.parentId === targetParent && (menu.order || 0) > targetOrder) {
                return { ...menu, order: (menu.order || 0) + 1 };
              }
              return menu;
            });
          }
        } 
        // 기존 동작 (position이 지정되지 않은 경우)
        else {
          updatedSourceMenu.order = targetOrder + 1;
          
          // 메뉴 배열 업데이트 (소스 메뉴 먼저 변경)
          state.menus = [
            ...state.menus.slice(0, sourceMenuIndex),
            updatedSourceMenu,
            ...state.menus.slice(sourceMenuIndex + 1)
          ];
          
          // 다른 형제 메뉴 순서 조정
          state.menus = state.menus.map(menu => {
            if (menu.id !== sourceId && menu.parentId === targetParent && (menu.order || 0) > targetOrder) {
              return { ...menu, order: (menu.order || 0) + 1 };
            }
            return menu;
          });
        }
      }
    },
    
    // 메뉴에 컴포넌트 연결
    assignComponentsToMenu: (state, action) => {
      const { menuId, componentIds } = action.payload;
      const menu = state.menus.find(menu => menu.id === menuId);
      if (menu) {
        menu.components = [...componentIds];
      }
    },
    
    // 메뉴 배열 전체 설정
    setMenus: (state, action) => {
      // 기존 메뉴들의 selectedMenuId 유지
      const selectedMenuId = state.selectedMenuId;
      state.menus = action.payload;
      state.selectedMenuId = selectedMenuId;
    }
  }
});

export const {
  addComponent,
  updateComponent,
  removeComponent,
  selectComponent,
  setProjectName,
  saveProject,
  loadProject,
  loadSavedProjects,
  deleteProject,
  loadComponents,
  updateComponentPosition,
  exportProjectToJSON,
  importProjectFromJSON,
  selectLayout,
  updateLayoutProps,
  resetLayout,
  addMenu,
  updateMenu,
  removeMenu,
  selectMenu,
  reorderMenus,
  assignComponentsToMenu,
  setMenus,
  applyPcSettingsToMobile
} = editorSlice.actions;

export const selectComponents = (state) => state.editor.components;
export const selectSelectedComponentId = (state) => state.editor.selectedComponentId;
export const selectSelectedComponent = (state) => {
  const id = state.editor.selectedComponentId;
  return id ? state.editor.components.find(comp => comp.id === id) : null;
};
export const selectCanvasSize = (state) => state.editor.canvasSize;
export const selectProjectName = (state) => state.editor.projectName;
export const selectSavedProjects = (state) => state.editor.savedProjects;
export const selectLayoutInfo = (state) => state.editor.layout;
export const selectMenus = (state) => state.editor.menus;
export const selectSelectedMenuId = (state) => state.editor.selectedMenuId;
export const selectSelectedMenu = (state) => {
  return state.editor.selectedMenuId 
    ? state.editor.menus.find(menu => menu.id === state.editor.selectedMenuId) 
    : null;
};

export const loadComponets = createAction('editor/loadComponents');

const editorReducer = createReducer(initialState, (builder) => {
  builder.addCase(loadComponents, (state, action) => {
    state.components = action.payload;
  });
});

export default editorSlice.reducer;